import React from 'react';
import PropTypes from 'prop-types';

import {Grid, Stack, Typography, Button} from 'views/components';
import {Refresh} from '@mui/icons-material';

import {convertToStandardTime} from 'utils/translate/date_formatter';
import {translate, Translate} from 'views/module';
import moment from 'moment';

/**
 * Updates the time from now text
 * @param {Object} momentObject Moment object to be used to calculate time displacement
 * @param {Number} interval number in milliseconds to set update interval
 * @param {*} refresh state that changes upon clicking refresh, which will update the timer
 * @return {String} time displacement string
 */
const FromNowUpdater = ({momentObject, interval, refresh}) => {
  const [timestamp, setTimestamp] = React.useState(null);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimestamp(`(${momentObject.fromNow()})`);
    }, interval);
    return () => clearInterval(timer);
  }, [refresh]);
  return timestamp;
};

/**
   * Refresh widget that contains a button to refresh a component and some text showing last updated time
   * @param {*} props
   * @param {Number} props.numRefresh state variable to update when clicking refresh
   * @param {Function} props.setNumRefresh function to update the state when clicking refresh
   * @return {ReactJSXElement}
   */
const RefreshWidget = (props) => {
  const [now, setNow] = React.useState(moment());

  const numRefresh = props.numRefresh;
  const setNumRefresh = props.setNumRefresh;
  const justifyContent = props.justifyContent || 'start';

  const lastUpdatedTime = convertToStandardTime(now);
  const updateInterval = 1000;

  const onRefresh = () => {
    setNumRefresh(numRefresh + 1);
    setNow(moment());
  };

  return (
    <Grid container justifyContent={justifyContent} sx={{wordBreak: 'break-word', textAlign: 'left'}}>
      <Grid item order={{xs: 1, md: justifyContent === 'start' ? 1 : 2}} sx={{my: 'auto'}}>
        <Button variant='outlined' onClick={onRefresh} >
          <Refresh />
          <Typography sx={{textTransform: 'uppercase', fontWeight: 500, pl: 1, color: 'black'}}>
            <Translate expression='generic.Refresh'/>
          </Typography>
        </Button>
      </Grid>
      <Grid item
        order={{xs: 2, md: justifyContent === 'start' ? 2 : 1}}
        sx={{
          display: 'flex',
          alignItems: 'center',
          pr: {md: justifyContent === 'start' ? 0 : 1, xs: 0},
          pl: {md: justifyContent === 'start' ? 1 : 0, xs: 1},
        }}
      >
        <Stack pr={{xs: 2, sm: 0}} sx={{flexDirection: {xs: 'col', md: 'row'}}}>
          <Typography sx={{fontSize: 12, pr: {sm: 0.5}}}>
            {`${translate('generic.LastUpdated', {updatedTime: lastUpdatedTime})} `}
          </Typography>
          <Typography sx={{fontSize: 12}}>
            <FromNowUpdater momentObject={now} interval={updateInterval} refresh={numRefresh} />
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
};
RefreshWidget.propTypes = {
  numRefresh: PropTypes.number.isRequired,
  setNumRefresh: PropTypes.func.isRequired,
  justifyContent: PropTypes.string,
};

export {RefreshWidget, FromNowUpdater};
